import {
  type Column,
  type ColumnDef,
  type ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  type OnChangeFn,
  type Row,
  type RowSelectionState,
  type SortingState,
  useReactTable,
  type VisibilityState,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { type ReactNode, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

/**
 * A sortable column header.
 *
 * The icon reports the direction actually in effect rather than a permanent
 * both-ways arrow: where the indicator is seeded from the server's default
 * ordering, a static icon would leave the reader unable to tell sorted from
 * unsorted on the one column that always is.
 *
 * Generic over the row type, so the admin lists share one header rather than
 * keeping a copy each.
 */
export function sortableHeader<TData>(label: string) {
  return function SortableHeader({ column }: { column: Column<TData, unknown> }) {
    const direction = column.getIsSorted();
    const Icon = direction === 'asc' ? ArrowUp : direction === 'desc' ? ArrowDown : ArrowUpDown;

    return (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(direction === 'asc')}
        className="h-auto p-0 font-medium text-muted-foreground hover:bg-transparent"
      >
        {label}
        <Icon className={`ml-2 h-4 w-4${direction ? '' : ' opacity-50'}`} />
      </Button>
    );
  };
}

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  rowSelection?: RowSelectionState;
  /**
   * Boolean enables/disables selection table-wide. Function form gates
   * selection per-row — TanStack respects this for both the per-row
   * checkbox AND the header "select all" toggle, so callers can exclude
   * specific rows (e.g. purged audit stubs) from batch operations.
   */
  enableRowSelection?: boolean | ((row: Row<TData>) => boolean);
  getRowId?: (row: TData) => string;
  /** Optional function to get additional class names for a row */
  getRowClassName?: (row: TData) => string | undefined;
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  /**
   * Called with a row's data when that row is clicked. Adds a pointer cursor
   * and keyboard operability when set.
   *
   * Prefer putting a real link or button inside the row's primary cell where
   * the interaction is navigation: that keeps middle-click and open-in-new-tab
   * working, which a click handler on the row cannot. This prop remains for
   * rows whose activation is not a navigation.
   */
  onRowClick?: (row: TData) => void;
  /**
   * Drops the table's own rounded border. Set this when the table is already
   * inside a Card, where the default border draws a second frame just inside
   * the card's own edge.
   */
  unframed?: boolean;
  /**
   * Shown in place of the rows when there are none. Mirrors DataCardList.
   * Takes a node rather than a string so a caller can bring its own spacing
   * and emphasis instead of the default centred line.
   */
  emptyMessage?: ReactNode;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  onRowSelectionChange,
  rowSelection: controlledRowSelection,
  enableRowSelection = false,
  getRowId,
  getRowClassName,
  sorting: controlledSorting,
  onSortingChange,
  onRowClick,
  unframed = false,
  emptyMessage,
}: DataTableProps<TData, TValue>) {
  const [internalSorting, setInternalSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [internalRowSelection, setInternalRowSelection] = useState<RowSelectionState>({});

  const sorting = controlledSorting ?? internalSorting;
  const setSorting = onSortingChange ?? setInternalSorting;
  const rowSelection = controlledRowSelection ?? internalRowSelection;
  const setRowSelection = onRowSelectionChange ?? setInternalRowSelection;

  // When onSortingChange is provided, we're doing server-side sorting
  // so we don't need getSortedRowModel (data comes pre-sorted from API)
  const isServerSideSorting = !!onSortingChange;

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    // Note: Server-side pagination is used, so no getPaginationRowModel needed
    onSortingChange: setSorting,
    // Only use client-side sorting when not doing server-side sorting
    getSortedRowModel: isServerSideSorting ? undefined : getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    enableRowSelection,
    getRowId,
    // Disable multi-sort for server-side sorting (API only supports single column)
    enableMultiSort: !isServerSideSorting,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  return (
    <div className={unframed ? 'overflow-x-auto' : 'rounded-md border'}>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && 'selected'}
                className={[
                  getRowClassName?.(row.original),
                  onRowClick ? 'cursor-pointer' : undefined,
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                // tabIndex without a role override: a `role="button"` here
                // would replace the row's implicit `row` role, making it an
                // invalid child of the `rowgroup` tbody. Screen readers would
                // lose row and column navigation on exactly the rows that are
                // interactive, and the accessible name would collapse into
                // every cell read as one string. The row stays a row and stays
                // reachable and operable from the keyboard.
                tabIndex={onRowClick ? 0 : undefined}
                onKeyDown={
                  onRowClick
                    ? (event) => {
                        // Only the row's own key events activate it. A row may
                        // contain a real link — the name on the users table,
                        // the reference on assessments — and that link's Enter
                        // bubbles to here. Without this check the row would
                        // preventDefault the link's own activation and
                        // navigate itself, and Space, which should do nothing
                        // on a link, would navigate too.
                        if (event.target !== event.currentTarget) {
                          return;
                        }
                        // Enter and Space are the conventional activation keys for a
                        // focusable row; Space must not also scroll the page.
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          onRowClick(row.original);
                        }
                      }
                    : undefined
                }
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                // The fixed height belongs to the default line only. A caller
                // supplying its own node brings its own spacing with it.
                className={emptyMessage === undefined ? 'h-24 text-center' : undefined}
              >
                {emptyMessage ?? 'No results.'}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
